import { Component, ChangeDetectionStrategy, signal, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "src/app/supabase/services/auth.service";
import { AuthFormComponent } from "../../components/auth-form/auth-form.component";
import { IonicModule, ToastController, Platform } from "@ionic/angular";
import { AuthRegisterFormComponent } from "../../components/auth-register-form/auth-register-form.component";
import { AuthResetPasswordFormComponent } from '../../components/auth-forgot-password-form/auth-forgot-password-form.component';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
@Component({
    selector: "app-auth",
    templateUrl: "./auth.component.html",
    styleUrls: ["./auth.component.scss"],
    imports: [AuthFormComponent, AuthRegisterFormComponent, AuthResetPasswordFormComponent, IonicModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthComponent implements OnInit {
  isSignIn = signal<boolean>(true);
  isResetPassword = signal<boolean>(false);
  loading = signal<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router,
    private toastController: ToastController,
    private platform: Platform
  ) {}

  ngOnInit() {
    if (this.platform.is('capacitor')) {
      Keyboard.setResizeMode({
        mode: KeyboardResize.None
      });
    }
    this.authService.authChanges((event, session) => {
      if (session && session.user && this.router.url === "/auth") {
        this.router.navigateByUrl("/home");
      }
    });
  }

  /**
   * Handle mode changes between sign-in, register, and reset password
   * @param mode 'signIn' | 'register' | 'resetPassword'
   */
  handleChangeMode(mode: 'signIn' | 'register' | 'resetPassword') {
    this.isSignIn.set(mode === 'signIn');
    this.isResetPassword.set(mode === 'resetPassword');
  }

  /**
   * Handle form submissions for sign-in, register, and reset password
   * @param creds User credentials or email
   */
  async handleSubmit(creds: any) {
    if (creds?.type === "submit") return;
    try {
      this.loading.set(true);
      let successToast;
      let response;

      if (this.isResetPassword()) {
        successToast = await this.toastController.create({
          message: "Falls deine E-Mail Adresse korrekt ist, erhältst du eine E-Mail mit einem Link zum Zurücksetzen des Passworts.",
          duration: 5000,
          color: "success",
        });
        response = await this.authService.sendPwReset(creds);
      } else {
        successToast = await this.toastController.create({
          message: this.isSignIn()
            ? "Anmeldung erfolgreich."
            : "Registrierung erfolgreich. Überprüfe dein E-Mail Postfach, um dein Konto zu aktivieren.",
          duration: 5000,
          color: "success",
        });
        response = this.isSignIn()
          ? await this.authService.signIn(creds)
          : await this.authService.signUp(creds);
      }

      const { data, error } = response;

      if (error) {
        await this.presentFailedLoginToast(data, error);
      } else {
        if (this.isResetPassword()) {
          successToast.present();
        } else if (this.isSignIn()) {
          this.router.navigateByUrl("/home");
        } else {
          successToast.present();
          this.isSignIn.set(true);
        }
      }
    } catch (error) {
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  async handleGoogleLogin() {
    await this.authService.signInWithGoogle();
  }

  async handleAppleLogin() {
    await this.authService.signInWithApple();
  }

  async handleMicrosoftLogin() {
    await this.authService.signInWithMicrosoft();
  }

  async presentFailedLoginToast(data: any, error: any) {
    let errorToast;

    if (error.message === "Invalid login credentials") {
      errorToast = await this.toastController.create({
        message: "Email oder Passwort nicht korrekt.",
        duration: 3000,
        color: "danger",
      });
    } else if (error.message === "Email not confirmed") {
      errorToast = await this.toastController.create({
        message: "Email nicht bestätigt. Überprüfe dein E-Mail Postfach.",
        duration: 3000,
        color: "danger",
      });
    } else {
      errorToast = await this.toastController.create({
        message: "Authentifizierung fehlgeschlagen.",
        duration: 3000,
        color: "danger",
      });
    }

    return errorToast.present();
  }

  handleForgotPassword() {}
}
